import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  FileText,
  Megaphone,
  Search,
} from 'lucide-react';

import PixelBackground from '@/components/PixelBackground';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import Seo from '@/components/Seo';
import { JsonLd } from '@/components/JsonLd';
import Display from '@/components/typography/Display';
import Button from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Container, HeroSection, Section } from '@/components/ui/Section';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocalizedPath, stripLanguagePrefix, withLanguagePrefix } from '@/lib/locale';
import { BASE_URL, getHreflangLinks } from '@/seo/meta';
import {
  generateBreadcrumbSchema,
  generateFAQSchema,
  generateOrganizationSchema,
  generateServiceSchema,
  generateWebsiteSchema,
} from '@/lib/seo';
import { trackCTA, trackServiceView } from '@/lib/analytics';
import { trackScroll } from '@/lib/scroll';
import { getServiceMeta } from '@/seo/meta';

const serviceName = 'Υπηρεσίες Marketing';
const serviceSlug = 'marketing-strategy';

type Locale = 'en' | 'el';

type HeroContent = {
  eyebrow: string;
  title: string;
  description: string;
  primaryCta: string;
  secondaryCta: string;
};

type SectionCopy = {
  eyebrow?: string;
  heading: string;
  description: string;
};

type BulletSection = SectionCopy & {
  rightTitle: string;
  bullets: string[];
};

type ProcessStep = {
  title: string;
  description: string;
  bullets: string[];
};

type CapabilityGroup = {
  title: string;
  bullets: string[];
};

type DeliverablesContent = {
  eyebrow: string;
  heading: string;
  description: string;
  bullets: string[];
  note: string;
};

type FaqItem = {
  question: string;
  answer: string;
};

type ClosingContent = {
  eyebrow?: string;
  title: string;
  description: string;
  primaryCta: string;
};

const heroCopy: Record<Locale, HeroContent> = {
  en: {
    eyebrow: 'Marketing Services',
    title: 'Marketing that drives steady demand, not just clicks.',
    description:
      'We help businesses show up where customers search and turn traffic into real inquiries and sales. SEO, advertising, and content work together with a clear plan and continuous improvement.',
    primaryCta: 'Book a free consultation',
    secondaryCta: 'See marketing work',
  },
  el: {
    eyebrow: 'Υπηρεσίες Marketing',
    title: 'Marketing που φέρνει σταθερή ζήτηση, όχι απλά clicks.',
    description:
      'Βοηθάμε επιχειρήσεις να εμφανίζονται εκεί που ψάχνουν οι πελάτες τους και να μετατρέπουν την επισκεψιμότητα σε πραγματικές επαφές και πωλήσεις. SEO, διαφήμιση και περιεχόμενο δουλεύουν μαζί, με ξεκάθαρο πλάνο και συνεχή βελτίωση.',
    primaryCta: 'Κλείστε δωρεάν συμβουλευτική',
    secondaryCta: 'Δείτε έργα marketing',
  },
};

const whatWeDoCopy: Record<Locale, BulletSection> = {
  en: {
    eyebrow: 'What we do in marketing',
    heading: 'What we do in marketing',
    description: "We don't just run campaigns. We build a foundation that brings the right customers again and again.",
    rightTitle: 'Specifically',
    bullets: [
      'Improve your presence on Google',
      'Set up ads with cost control',
      'Create content that explains and persuades',
      "Measure what works and what doesn't",
    ],
  },
  el: {
    eyebrow: 'Τι κάνουμε στο marketing',
    heading: 'Τι κάνουμε στο marketing',
    description: 'Δεν «τρέχουμε απλά καμπάνιες». Στήνουμε βάση για να έρχονται σωστοί πελάτες, ξανά και ξανά.',
    rightTitle: 'Συγκεκριμένα',
    bullets: [
      'Βελτιώνουμε την παρουσία σας στη Google',
      'Στήνουμε διαφημίσεις με έλεγχο κόστους',
      'Δημιουργούμε περιεχόμενο που εξηγεί και πείθει',
      'Μετράμε τι δουλεύει και τι όχι',
    ],
  },
};

const processIntroCopy: Record<Locale, SectionCopy> = {
  en: {
    eyebrow: 'How we work',
    heading: 'How we work',
    description: 'A simple, practical model without unnecessary complexity.',
  },
  el: {
    eyebrow: 'Πώς δουλεύουμε',
    heading: 'Πώς δουλεύουμε',
    description: 'Ένα απλό, πρακτικό μοντέλο χωρίς περιττή πολυπλοκότητα',
  },
};

const processCopy: Record<Locale, ProcessStep[]> = {
  en: [
    {
      title: 'Business understanding',
      description: 'We learn:',
      bullets: [
        'What you sell',
        'Who you sell to',
        'Who your competitors are',
        'What your goal is (inquiries, appointments, sales)',
      ],
    },
    {
      title: 'Foundation setup',
      description: 'We put the basics in place:',
      bullets: [
        'SEO for the right keywords',
        'Core content structure',
        'Measurement setup (visits, forms, calls)',
      ],
    },
    {
      title: 'Advertising & amplification',
      description: 'We reinforce results where it makes sense:',
      bullets: [
        'Google Ads or Social Ads',
        'Simple, clear messaging',
        'Cost and performance control',
      ],
    },
    {
      title: 'Improvement & evolution',
      description: 'We keep refining:',
      bullets: [
        'We see what drives results',
        "We fix what doesn't work",
        'We build stronger performance over time',
      ],
    },
  ],
  el: [
    {
      title: 'Κατανόηση επιχείρησης',
      description: 'Μαθαίνουμε:',
      bullets: [
        'τι πουλάτε',
        'σε ποιον',
        'ποιος είναι ο ανταγωνισμός',
        'τι στόχο έχετε (επικοινωνία, ραντεβού, πωλήσεις)',
      ],
    },
    {
      title: 'Στήσιμο βάσης',
      description: 'Στήνουμε τα βασικά:',
      bullets: [
        'SEO για τις σωστές λέξεις',
        'βασική δομή περιεχομένου',
        'ρύθμιση μετρήσεων (επισκέψεις, φόρμες, κλήσεις)',
      ],
    },
    {
      title: 'Διαφήμιση & ενίσχυση',
      description: 'Ενισχύουμε όπου έχει νόημα:',
      bullets: [
        'Google Ads ή Social Ads όπου έχει νόημα',
        'απλά, ξεκάθαρα μηνύματα',
        'έλεγχος κόστους και απόδοσης',
      ],
    },
    {
      title: 'Βελτίωση & εξέλιξη',
      description: 'Συνεχής βελτίωση:',
      bullets: [
        'βλέπουμε τι φέρνει αποτέλεσμα',
        'διορθώνουμε ό,τι δεν δουλεύει',
        'χτίζουμε σταδιακά καλύτερη απόδοση',
      ],
    },
  ],
};

const capabilitiesCopy: Record<Locale, { eyebrow: string; heading: string; description: string; groups: CapabilityGroup[] }> = {
  en: {
    eyebrow: 'What we handle',
    heading: 'What we handle',
    description: 'Clear scope with everything needed to drive growth.',
    groups: [
      {
        title: 'SEO & visibility',
        bullets: [
          'Keyword research based on customer searches',
          'Website optimization',
          'Content that helps people find you',
        ],
      },
      {
        title: 'Advertising',
        bullets: ['Google Ads', 'Social Media Ads', 'Campaign setup and monitoring'],
      },
      {
        title: 'Content',
        bullets: ['Copy for sites and landing pages', 'Articles and guides', 'Materials that support sales'],
      },
      {
        title: 'Measurement & clarity',
        bullets: ['Analytics setup', 'Simple reports without technical jargon', 'Clear view of what drives results'],
      },
    ],
  },
  el: {
    eyebrow: 'Τι αναλαμβάνουμε',
    heading: 'Τι αναλαμβάνουμε',
    description: 'Ξεκάθαρο scope για όσα χρειάζεστε για ανάπτυξη.',
    groups: [
      {
        title: 'SEO & Ορατότητα',
        bullets: [
          'Έρευνα λέξεων που ψάχνουν οι πελάτες σας',
          'Βελτιστοποίηση ιστοσελίδας',
          'Περιεχόμενο που βοηθά να σας βρουν',
        ],
      },
      {
        title: 'Διαφήμιση',
        bullets: ['Google Ads', 'Social Media Ads', 'Ρύθμιση και παρακολούθηση καμπανιών'],
      },
      {
        title: 'Περιεχόμενο',
        bullets: ['Κείμενα για site και landing pages', 'Άρθρα και οδηγοί', 'Υλικό που υποστηρίζει πωλήσεις'],
      },
      {
        title: 'Μετρήσεις & εικόνα',
        bullets: [
          'Ρύθμιση analytics',
          'Απλά reports, χωρίς τεχνικούς όρους',
          'Ξεκάθαρη εικόνα για το τι φέρνει αποτέλεσμα',
        ],
      },
    ],
  },
};

const deliverablesCopy: Record<Locale, DeliverablesContent> = {
  en: {
    eyebrow: 'Deliverables',
    heading: 'What we deliver',
    description: 'Everything is set up to be ready for action and improvement.',
    bullets: [
      'Clear action plan',
      'Proper setup of measurement tools',
      'Campaigns and content ready to use',
      'Ongoing support and improvements',
    ],
    note: "No generic reports. You know what's happening and why.",
  },
  el: {
    eyebrow: 'Τι παραδίδουμε',
    heading: 'Τι παραδίδουμε',
    description: 'Όλα είναι έτοιμα για χρήση και βελτίωση.',
    bullets: [
      'Συγκεκριμένο πλάνο ενεργειών',
      'Σωστή ρύθμιση εργαλείων μέτρησης',
      'Καμπάνιες και περιεχόμενο έτοιμα για χρήση',
      'Συνεχή υποστήριξη και βελτιώσεις',
    ],
    note: 'Χωρίς γενικόλογα reports. Ξέρετε τι γίνεται και γιατί.',
  },
};

const faqIntroCopy: Record<Locale, SectionCopy> = {
  en: {
    eyebrow: 'FAQ',
    heading: 'Frequently asked questions',
    description: 'Everything you need to know before starting.',
  },
  el: {
    eyebrow: 'Συχνές Ερωτήσεις',
    heading: 'Συχνές Ερωτήσεις',
    description: 'Όσα χρειάζεται να γνωρίζετε πριν ξεκινήσετε.',
  },
};

const faqCopy: Record<Locale, FaqItem[]> = {
  en: [
    {
      question: 'When do the first results appear?',
      answer: 'Usually within the first 1–3 months, depending on the channel and competition.',
    },
    {
      question: 'Do you manage ads as well?',
      answer: 'Yes. From setup to monitoring and optimization.',
    },
    {
      question: 'Do I get reports?',
      answer: 'Yes, simple and understandable. You know what you pay for and what drives results.',
    },
    {
      question: 'Can this be combined with website or branding?',
      answer: 'Yes. Marketing often works best with a strong website and clear messaging.',
    },
  ],
  el: [
    {
      question: 'Πότε φαίνονται τα πρώτα αποτελέσματα;',
      answer: 'Συνήθως μέσα στους πρώτους 1–3 μήνες, ανάλογα το κανάλι και τον ανταγωνισμό.',
    },
    {
      question: 'Αναλαμβάνετε και τις διαφημίσεις;',
      answer: 'Ναι. Από το στήσιμο μέχρι την παρακολούθηση και τη βελτίωση.',
    },
    {
      question: 'Παίρνω αναφορές;',
      answer: 'Ναι, απλές και κατανοητές. Ξέρετε τι πληρώνετε και τι φέρνει αποτέλεσμα.',
    },
    {
      question: 'Μπορεί να συνδυαστεί με website ή branding;',
      answer: 'Ναι. Συχνά το marketing δουλεύει καλύτερα όταν συνδυάζεται με σωστή ιστοσελίδα και καθαρό μήνυμα.',
    },
  ],
};

const closingCopy: Record<Locale, ClosingContent> = {
  en: {
    eyebrow: 'Next step',
    title: 'Want your marketing to work more clearly?',
    description: 'We review what you have today and propose the next steps.',
    primaryCta: 'Book a free consultation',
  },
  el: {
    eyebrow: 'Επόμενο βήμα',
    title: 'Θέλετε το marketing σας να δουλεύει πιο καθαρά;',
    description: 'Μιλάμε, βλέπουμε τι έχετε σήμερα και σας προτείνουμε τα επόμενα βήματα.',
    primaryCta: 'Κλείστε δωρεάν συμβουλευτική',
  },
};

const capabilityIcons: LucideIcon[] = [Search, Megaphone, FileText, BarChart3];

export default function Marketing() {
  const { language } = useLanguage();
  const location = useLocation();
  const locale: Locale = language === 'el' ? 'el' : 'en';
  const localizedPath = useLocalizedPath();

  const metaData = getServiceMeta(serviceSlug, locale);
  const canonicalPath = withLanguagePrefix(stripLanguagePrefix(location.pathname), locale);
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const hreflang = getHreflangLinks(location.pathname, locale);
  const serviceDisplayName = locale === 'en' ? 'Marketing Services' : serviceName;

  const hero = heroCopy[locale];
  const whatWeDo = whatWeDoCopy[locale];
  const processIntro = processIntroCopy[locale];
  const processSteps = processCopy[locale];
  const capabilities = capabilitiesCopy[locale];
  const deliverables = deliverablesCopy[locale];
  const faqIntro = faqIntroCopy[locale];
  const faqItems = faqCopy[locale];
  const closing = closingCopy[locale];

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    trackServiceView(serviceSlug, language);
    const cleanup = trackScroll({ threshold: 0.75, once: true });
    return cleanup;
  }, [language]);

  const handleCTA = (ctaText: string) => {
    trackCTA(ctaText, window.location.pathname);
    window.navigateToConsultation?.();
  };

  const toggleFaq = (index: number) => {
    setOpenFaqIndex((current) => (current === index ? null : index));
  };

  return (
    <>
      <Seo
        title={metaData.title}
        description={metaData.description}
        url={canonicalUrl}
        image={metaData.image}
        hreflang={hreflang}
      />

      <JsonLd json={generateOrganizationSchema()} />
      <JsonLd json={generateWebsiteSchema(locale)} />
      <JsonLd json={generateBreadcrumbSchema(serviceDisplayName, serviceSlug, locale)} />
      <JsonLd json={generateServiceSchema(serviceDisplayName, metaData.description, canonicalUrl, 'Marketing')} />
      <JsonLd json={generateFAQSchema(faqItems)} />

      <div className="relative min-h-screen bg-[#0b0a0e] text-white">
        <PixelBackground />
        <Header variant="simple" />

        <main id="main-content" className="relative z-10 overflow-hidden">
          <HeroSection className="relative overflow-hidden pt-32 pb-20 md:pt-36 lg:pt-40 lg:pb-24">
            <div className="absolute inset-0 geometric-pattern" aria-hidden="true" />
            <div
              className="absolute inset-0 bg-gradient-to-br from-pp-black/95 via-black/85 to-black"
              aria-hidden="true"
            />
            <Container className="relative lg:min-h-[calc(100vh-220px)] lg:flex lg:flex-col lg:justify-center">
              <div className="max-w-3xl space-y-8">
                <div className="space-y-5">
                  <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-highlight/70">
                    {hero.eyebrow}
                  </p>
                  <Display
                    variant="h1"
                    as="h1"
                    className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.12] tracking-tight"
                  >
                    {hero.title}
                  </Display>
                </div>

                <Display
                  variant="subhead"
                  as="p"
                  className="max-w-2xl text-base sm:text-lg leading-relaxed text-white/70"
                >
                  {hero.description}
                </Display>

                <div className="flex flex-col gap-4 sm:flex-row">
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-full justify-center sm:w-auto"
                    onClick={() => handleCTA(hero.primaryCta)}
                  >
                    {hero.primaryCta}
                  </Button>
                  <Button
                    as="a"
                    href={localizedPath('/projects')}
                    variant="secondary"
                    size="lg"
                    className="w-full justify-center sm:w-auto"
                  >
                    {hero.secondaryCta}
                  </Button>
                </div>
              </div>
            </Container>
          </HeroSection>

          <Section>
            <Container>
              <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_1px_minmax(0,1.05fr)] lg:items-start lg:gap-12">
                <div className="space-y-4">
                  {whatWeDo.eyebrow ? (
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                      {whatWeDo.eyebrow}
                    </p>
                  ) : null}
                  <h2 className="text-3xl font-semibold text-white md:text-5xl">{whatWeDo.heading}</h2>
                  <p className="text-lg text-white/80">{whatWeDo.description}</p>
                </div>

                <div className="hidden lg:block h-full w-px bg-gradient-to-b from-transparent via-white/10 to-transparent" />

                <Card className="relative overflow-hidden rounded-[28px] border-white/15 bg-black/70 backdrop-blur-xl p-6 sm:p-7 shadow-glass">
                  <div className="pointer-events-none absolute -right-24 -top-20 h-44 w-44 rounded-full bg-brand-highlight/15 blur-3xl" />
                  <div className="pointer-events-none absolute -bottom-24 -left-16 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
                  <div className="relative space-y-6">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-white/50">
                      {whatWeDo.rightTitle}
                    </p>
                    <ul className="space-y-4 text-sm text-white/70">
                      {whatWeDo.bullets.map((bullet) => (
                        <li key={bullet} className="flex items-start gap-3">
                          <span className="mt-2 h-2 w-2 rounded-full bg-brand-highlight/80" />
                          <span className="leading-relaxed">{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              </div>
            </Container>
          </Section>

          <Section id="process" className="relative isolate">
            <div className="absolute inset-0 geometric-pattern opacity-20" aria-hidden="true" />
            <Container className="relative">
              <div className="max-w-3xl space-y-4 text-center md:mx-auto">
                {processIntro.eyebrow ? (
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                    {processIntro.eyebrow}
                  </p>
                ) : null}
                <h2 className="text-3xl font-semibold text-white md:text-5xl">{processIntro.heading}</h2>
                <p className="text-lg text-white/70">{processIntro.description}</p>
              </div>

              <div className="mt-16 grid gap-8 lg:grid-cols-2">
                {processSteps.map((step, index) => (
                  <div
                    key={step.title}
                    className="group flex h-full flex-col gap-4 rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-8 shadow-glass transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                  >
                    <div className="flex items-center gap-4">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-lg font-semibold text-white transition group-hover:border-brand-highlight/60 group-hover:text-brand-highlight">
                        {(index + 1).toString().padStart(2, '0')}
                      </span>
                      <h3 className="text-2xl font-semibold text-white">{step.title}</h3>
                    </div>
                    <p className="text-sm text-white/70">{step.description}</p>
                    <ul className="mt-4 space-y-2 text-sm text-white/70">
                      {step.bullets.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-brand-highlight/70" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </Container>
          </Section>

          <Section>
            <Container>
              <div className="max-w-3xl space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {capabilities.eyebrow}
                </p>
                <h2 className="text-3xl font-semibold text-white md:text-5xl">{capabilities.heading}</h2>
                <p className="text-lg text-white/70">{capabilities.description}</p>
              </div>

              <div className="mt-12 grid gap-8 md:grid-cols-2">
                {capabilities.groups.map((group, index) => {
                  const Icon = capabilityIcons[index] ?? Search;
                  return (
                    <Card
                      key={group.title}
                      className="group space-y-5 rounded-2xl border-white/15 bg-black/70 backdrop-blur-xl p-8 shadow-glass transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-brand-highlight/80 transition group-hover:border-brand-highlight/60 group-hover:text-brand-highlight">
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className="text-2xl font-semibold text-white">{group.title}</h3>
                      <ul className="space-y-3 text-sm text-white/75">
                        {group.bullets.map((bullet) => (
                          <li key={bullet} className="flex items-start gap-3">
                            <CheckCircle className="mt-0.5 h-4 w-4 text-brand-highlight/70" />
                            <span className="leading-relaxed">{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  );
                })}
              </div>
            </Container>
          </Section>

          <Section>
            <Container>
              <Card className="relative overflow-hidden rounded-[28px] border-white/15 bg-black/70 backdrop-blur-xl p-8 shadow-glass">
                <div className="pointer-events-none absolute -right-24 -top-20 h-44 w-44 rounded-full bg-brand-highlight/15 blur-3xl" />
                <div className="relative space-y-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                    {deliverables.eyebrow}
                  </p>
                  <h2 className="text-3xl font-semibold text-white md:text-5xl">{deliverables.heading}</h2>
                  <p className="text-lg text-white/70">{deliverables.description}</p>
                  <ul className="space-y-3 text-sm text-white/75">
                    {deliverables.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-3">
                        <CheckCircle className="mt-0.5 h-4 w-4 text-brand-highlight/70" />
                        <span className="leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-white/55">{deliverables.note}</p>
                </div>
              </Card>
            </Container>
          </Section>

          <Section>
            <Container className="max-w-5xl">
              <div className="text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {faqIntro.eyebrow}
                </p>
                <h2 className="mt-4 text-3xl font-semibold text-white md:text-5xl">{faqIntro.heading}</h2>
                <p className="mt-4 text-lg text-white/70">{faqIntro.description}</p>
              </div>

              <div className="mt-12 space-y-4">
                {faqItems.map((faq, index) => {
                  const open = openFaqIndex === index;
                  return (
                    <div
                      key={faq.question}
                      className="overflow-hidden rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                    >
                      <button
                        type="button"
                        onClick={() => toggleFaq(index)}
                        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left text-base font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40"
                      >
                        <span>{faq.question}</span>
                        {open ? (
                          <ChevronUp className="h-5 w-5 text-white/60" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-white/60" />
                        )}
                      </button>
                      {open && (
                        <div className="border-t border-white/10 px-6 py-5 text-sm text-white/70">
                          <p className="leading-relaxed">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Container>
          </Section>

          <Section className="pt-0">
            <Container>
              <div className="relative overflow-hidden rounded-[32px] border border-white/15 bg-black/70 p-12 shadow-glass">
                <div className="pointer-events-none absolute -right-24 -top-32 h-72 w-72 rounded-full bg-brand-highlight/30 blur-3xl opacity-60" />
                <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-white/10 blur-3xl opacity-60" />
                <div className="relative text-center">
                  {closing.eyebrow ? (
                    <p className="text-xs font-semibold uppercase tracking-[0.35em] text-brand-highlight/80">
                      {closing.eyebrow}
                    </p>
                  ) : null}
                  <Display
                    variant="h1"
                    as="h2"
                    className="mx-auto mt-4 max-w-4xl text-balance text-xl font-semibold text-white md:text-3xl"
                  >
                    {closing.title}
                  </Display>
                  <p className="mx-auto mt-4 max-w-3xl text-center text-lg text-white/70">
                    {closing.description}
                  </p>

                  <div className="mt-10 flex flex-col gap-4 text-center sm:flex-row sm:justify-center">
                    <Button
                      variant="primary"
                      size="lg"
                      className="w-full justify-center sm:w-auto"
                      onClick={() => handleCTA(closing.primaryCta)}
                    >
                      {closing.primaryCta}
                    </Button>
                  </div>
                </div>
              </div>
            </Container>
          </Section>
        </main>

        <Footer variant="compact" />
      </div>
    </>
  );
}
